<script lang="ts">
  /* THE DEPARTURE PLANNER — the flagship.
   *
   * The same route, solved at every hour across the window, ranked by how long
   * the passage actually takes. Nobody else does this, and the odd part is that
   * the router was always there: the only thing missing was a loop and somewhere
   * to put the answers.
   *
   * ═══ THE FIGURE IS THE BIGGEST THING IN THE ROW ═══
   *
   * Because the figure is the answer. Everything else — the departure, the
   * chip, the reason — is what you read second, to find out why. A list of
   * equal-weight rows is a table, and a table is what savvy navvy already
   * shows.
   *
   * ═══ AND IT FILLS IN AS IT GOES ═══
   *
   * Forty-eight worker solves take a few seconds. A planner that shows nothing
   * and then everything is one people assume has hung, so the rows arrive in
   * departure order and re-sort as the ranking firms up. The progress line says
   * how far through it is rather than spinning.
   */
  import { describeDeparture, type DepartureWindow } from '@gennetay/chart-core/lib/departure';
  import Button from '../primitives/Button.svelte';
  import Chip from '../primitives/Chip.svelte';
  import Empty from '../primitives/Empty.svelte';
  import Notice from '../primitives/Notice.svelte';
  import SectionHeader from '../primitives/SectionHeader.svelte';

  let {
    options = [],
    /** How many of the candidates have been solved, and how many there are. */
    done = 0,
    total = 0,
    /** Set the trip's departure to this one. */
    onpick,
    /** Ask for the whole window again — after a speed or draft change, which
     *  re-asks the question rather than adjusting the answer. */
    onreplan,
    /** Why there is nothing to plan: no route yet, no current data on this
     *  host. Same rule the layers panel follows — a panel that silently shows
     *  nothing is worse than one that says why. */
    unavailable = null
  }: {
    options?: readonly DepartureWindow[];
    done?: number;
    total?: number;
    onpick?: (t: number) => void;
    onreplan?: () => void;
    unavailable?: string | null;
  } = $props();

  const running = $derived(total > 0 && done < total);

  const hhmm = (t: number) =>
    new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  const dayOf = (t: number) => new Date(t).toLocaleDateString([], { weekday: 'short' });

  /** "4h12". Durations read as hours and minutes; a bare minute count past an
   *  hour is arithmetic the reader has to do. */
  function fmt(ms: number | null): string {
    if (ms === null) return '—';
    const m = Math.round(ms / 60_000);
    return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}`;
  }
</script>

{#if unavailable}
  <Empty title="Nothing to plan" body={unavailable} />
{:else}
  <p class="lede">
    The same route, solved at every hour across the window and ranked by how long it actually
    takes — not by when you asked to leave.
  </p>

  {#if running}
    <!-- Progress rather than a spinner: "17 of 48" is a fact, and a spinner is
         a promise. -->
    <div class="prog" role="status">
      <span class="bar"><span style="width: {(done / total) * 100}%"></span></span>
      <span class="n">{done} of {total}</span>
    </div>
  {/if}

  {#if options.length === 0 && !running}
    <Empty
      title="No departures solved"
      body="Set a route and a boat speed, then ask for the window. Each candidate is a full solve, so it takes a moment."
    >
      {#snippet action()}
        {#if onreplan}<Button intent="primary" onclick={onreplan}>Plan the window</Button>{/if}
      {/snippet}
    </Empty>
  {:else}
    <SectionHeader title="Departures" />
    <div class="opts">
      {#each options as o (o.departT)}
        <button
          class="opt"
          class:best={o.best}
          class:blocked={o.elapsedMs === null}
          disabled={o.elapsedMs === null}
          onclick={() => onpick?.(o.departT)}
        >
          <!-- ═══ A WINDOW, NOT AN INSTANT ═══
               "Anything between 11:00 and 19:00 gets you there in 3h18" is a
               decision you can act on with the rest of the morning free. An
               instant is a departure you now have to hit. The first version
               listed every candidate hour and showed seven consecutive rows
               reading the same thing. -->
          <span class="when">
            <span class="dy">{dayOf(o.departT)}</span>
            <span class="hh">{hhmm(o.departT)}</span>
            {#if o.untilT > o.departT}
              <span class="til">to {hhmm(o.untilT)}{#if dayOf(o.untilT) !== dayOf(o.departT)}
                  {dayOf(o.untilT)}{/if}</span>
            {/if}
          </span>

          <span class="fig">{fmt(o.elapsedMs)}</span>

          <span class="meta">
            {#if o.elapsedMs === null}
              <Chip>no route</Chip>
            {:else if o.waitMs <= 0}
              <Chip tone="on">no wait</Chip>
            {:else}
              <Chip tone="warn">holds {fmt(o.waitMs)}</Chip>
            {/if}
            <!-- Not "12% slower". A number in minutes is one you can act on;
                 a percentage of a duration is one you have to convert back. -->
            {#if o.worseByMs !== null && o.worseByMs > 0}
              <span class="why">+{fmt(o.worseByMs)} on the best</span>
            {:else}
              <span class="why">{describeDeparture(o)}</span>
            {/if}
          </span>
        </button>
      {/each}
    </div>
  {/if}

  {#if onreplan && options.length > 0}
    <div class="again"><Button onclick={onreplan}>Plan the window again</Button></div>
  {/if}

  <Notice>
    Ranked on predicted streams and a speed you entered. A departure past the current model's reach
    is timed on the narrows and boat speed alone, and the row says so.
  </Notice>
{/if}

<style>
  .lede {
    margin: 0 0 var(--sp-5);
    font: var(--fs-read) / var(--lh-read) var(--sans);
    color: var(--sub);
  }

  .prog {
    display: flex;
    align-items: center;
    gap: var(--sp-4);
    margin-bottom: var(--sp-5);
  }
  .bar {
    flex: 1;
    height: 3px;
    border-radius: var(--r-pill);
    background: var(--s-2);
    overflow: hidden;
  }
  .bar span {
    display: block;
    height: 100%;
    background: var(--cy);
    transition: width var(--dur-base) var(--ease-out);
  }
  .n {
    flex: none;
    font: var(--fs-body) / 1 var(--mono);
    color: var(--sub);
    font-variant-numeric: tabular-nums;
  }

  .opts {
    display: flex;
    flex-direction: column;
    gap: var(--sp-3);
  }
  .opt {
    display: grid;
    grid-template-columns: 5.5em 1fr;
    grid-template-areas:
      'when fig'
      'when meta';
    align-items: center;
    gap: var(--sp-1) var(--sp-5);
    text-align: left;
    padding: var(--sp-4);
    background: var(--s-2);
    border: 1px solid var(--line);
    border-radius: var(--r-2);
    cursor: pointer;
    min-height: var(--h-touch);
  }
  .opt:hover:not(:disabled) {
    border-color: var(--sub);
  }
  .opt:focus-visible {
    outline: 2px solid var(--cy);
    outline-offset: 2px;
  }
  .opt.best {
    border-color: var(--cy);
    background: var(--cy-weak);
  }
  /* Kept in the list, greyed. "There is no way through before Tuesday" is an
     answer, and a list that silently omits half the day looks complete. */
  .opt.blocked {
    opacity: 0.5;
    cursor: default;
  }

  .when {
    grid-area: when;
    display: flex;
    flex-direction: column;
    gap: 2px;
    /* Wide enough for "to 19:00" without wrapping — the column is fixed so the
       figures beside it line up down the list. */
    min-width: 5.5em;
  }
  .dy {
    font: var(--fs-label) / 1 var(--px);
    letter-spacing: var(--tr-label);
    color: var(--sub);
    text-transform: uppercase;
  }
  .hh {
    font: var(--fs-body) / 1 var(--mono);
    color: var(--ink);
    font-variant-numeric: tabular-nums;
  }
  /* Subordinate to the opening time: the window's START is the answer, and its
     end is how much slack you have around it. */
  .til {
    font: var(--fs-micro) / 1.3 var(--mono);
    color: var(--sub);
    font-variant-numeric: tabular-nums;
  }

  .fig {
    grid-area: fig;
    font: var(--fs-num) / var(--lh-tight) var(--mono);
    color: var(--ink);
    font-variant-numeric: tabular-nums;
  }
  .opt.best .fig {
    font-size: var(--fs-num-lg);
  }

  .meta {
    grid-area: meta;
    display: flex;
    align-items: center;
    gap: var(--sp-4);
    flex-wrap: wrap;
  }
  .why {
    font: var(--fs-micro) / 1.4 var(--sans);
    color: var(--sub);
  }

  .again {
    margin-top: var(--sp-5);
  }
</style>
